// src/utils/type.util.ts
function isUndefined(value) {
  return value === void 0;
}
function isNull(value) {
  return value === null;
}

// src/utils/misc.util.ts
function defaultValue(value, def) {
  if (isUndefined(value) || isNull(value))
    return def;
  return value;
}
function objectsSum(first, second) {
  const output = {};
  for (const key in first) {
    if (Object.prototype.hasOwnProperty.call(first, key) && Object.prototype.hasOwnProperty.call(second, key)) {
      output[key] = first[key] + second[key];
    }
  }
  return output;
}

// src/utils/canvas.util.ts
function dimensionsToSides(dimensions, padding = {}) {
  return {
    left: dimensions.left + defaultValue(padding.left, 0),
    top: dimensions.top + defaultValue(padding.top, 0),
    right: dimensions.left + dimensions.width - defaultValue(padding.right, 0),
    bottom: dimensions.top + dimensions.height - defaultValue(padding.bottom, 0)
  };
}

// src/core/PdfPreviewWorker.ts
var _PdfPreviewWorker = class {
  constructor(canvas) {
    this.canvas = canvas;
    this._settings = {
      showBleed: true,
      showSafeZone: false
    };
    const ctx = canvas.getContext("2d");
    if (!ctx)
      throw new Error("Failed to get 2d context.");
    this.ctx = ctx;
  }
  set settings(settings) {
    this._settings = settings;
  }
  get settings() {
    return this._settings;
  }
  static async loadImage(url) {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => resolve(image);
      image.src = url;
    });
  }
  getCanvasDimensions() {
    return {
      top: 0,
      left: 0,
      width: this.ctx.canvas.width,
      height: this.ctx.canvas.height
    };
  }
  fillPadding(padding, dimensions) {
    const dim = dimensions || this.getCanvasDimensions();
    const outside = dimensionsToSides(dim);
    const inside = dimensionsToSides(dim, padding);
    this.ctx.beginPath();
    this.ctx.moveTo(outside.left, outside.top);
    this.ctx.lineTo(outside.right, outside.top);
    this.ctx.lineTo(outside.right, outside.bottom);
    this.ctx.lineTo(outside.left, outside.bottom);
    this.ctx.lineTo(outside.left, outside.top);
    this.ctx.closePath();
    this.ctx.moveTo(inside.left, inside.top);
    this.ctx.lineTo(inside.left, inside.bottom);
    this.ctx.lineTo(inside.right, inside.bottom);
    this.ctx.lineTo(inside.right, inside.top);
    this.ctx.lineTo(inside.left, inside.top);
    this.ctx.closePath();
    this.ctx.fill();
  }
  strokePaddingBorder(padding, dimensions) {
    const sides = dimensionsToSides(dimensions || this.getCanvasDimensions(), padding);
    const points = [
      {
        left: sides.right,
        top: sides.top,
        enabled: Boolean(padding.top)
      },
      {
        left: sides.right,
        top: sides.bottom,
        enabled: Boolean(padding.right)
      },
      {
        left: sides.left,
        top: sides.bottom,
        enabled: Boolean(padding.bottom)
      },
      {
        left: sides.left,
        top: sides.top,
        enabled: Boolean(padding.left)
      }
    ];
    this.ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (!point.enabled)
        continue;
      const previous = points[i === 0 ? points.length - 1 : i - 1];
      if (!previous.enabled || i === 0)
        this.ctx.moveTo(previous.left, previous.top);
      this.ctx.lineTo(point.left, point.top);
      this.ctx.moveTo(point.left, point.top);
    }
    this.ctx.closePath();
    this.ctx.stroke();
  }
  drawBleed(bleed, dimensions) {
    const dim = dimensions || this.getCanvasDimensions();
    this.ctx.save();
    this.ctx.fillStyle = _PdfPreviewWorker.CONFIGURATION.BLEED.FILL;
    this.ctx.lineWidth = _PdfPreviewWorker.CONFIGURATION.OUTLINE.WIDTH;
    this.ctx.setLineDash(_PdfPreviewWorker.CONFIGURATION.OUTLINE.SEGMENTS);
    this.ctx.strokeStyle = _PdfPreviewWorker.CONFIGURATION.BLEED.STROKE;
    this.fillPadding(bleed, dim);
    this.strokePaddingBorder(bleed, dim);
    this.ctx.restore();
  }
  drawSafeZone(safeZone, dimensions) {
    const dim = dimensions || this.getCanvasDimensions();
    this.ctx.save();
    this.ctx.fillStyle = _PdfPreviewWorker.CONFIGURATION.SAFE_ZONE.FILL;
    this.ctx.lineWidth = _PdfPreviewWorker.CONFIGURATION.OUTLINE.WIDTH;
    this.ctx.setLineDash(_PdfPreviewWorker.CONFIGURATION.OUTLINE.SEGMENTS);
    this.ctx.strokeStyle = _PdfPreviewWorker.CONFIGURATION.SAFE_ZONE.STROKE;
    this.fillPadding(safeZone, dim);
    this.strokePaddingBorder(safeZone, dim);
    this.ctx.restore();
  }
  drawHighlight(position) {
    this.ctx.save();
    this.ctx.fillStyle = _PdfPreviewWorker.CONFIGURATION.BLEED.FILL;
    this.fillPadding({
      top: this.canvas.height - position.top,
      left: position.left,
      right: this.canvas.width - position.right,
      bottom: position.bottom
    });
    this.ctx.restore();
  }
};
var PdfPreviewWorker = _PdfPreviewWorker;
PdfPreviewWorker.CONFIGURATION = {
  BLEED: {
    FILL: "#00000033",
    STROKE: "#000000"
  },
  SAFE_ZONE: {
    FILL: "#ff000033",
    STROKE: "#ff0000"
  },
  OUTLINE: {
    WIDTH: 1,
    SEGMENTS: [6, 4]
  },
  SHADOW: {
    COLOR: "rgba(0,0,0,.65)",
    BLUR: 10
  }
};

// src/core/PdfCoverPreviewWorker.ts
var PdfCoverPreviewWorker = class extends PdfPreviewWorker {
  constructor(canvas, data, image) {
    super(canvas);
    this.canvas = canvas;
    this.data = data;
    this.image = image;
  }
  static async init(canvas, data) {
    const image = await PdfPreviewWorker.loadImage(data.imageUrl);
    return new PdfCoverPreviewWorker(canvas, data, image);
  }
  render(highlight) {
    this.canvas.width = this.data.dimensions.width;
    this.canvas.height = this.data.dimensions.height;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(this.image, 0, 0, this.canvas.width, this.canvas.height);
    if (!isUndefined(highlight)) {
      const section = this.data.sections[highlight];
      if (section)
        this.drawHighlight(section);
    }
    if (this.settings.showSafeZone) {
      this.drawSafeZone(this.data.safeZone);
    }
    if (this.settings.showBleed) {
      this.drawBleed(this.data.bleed);
    }
  }
};

// src/core/ImageCache.ts
var ImageCache = class {
  constructor() {
    this.cache = /* @__PURE__ */ new Map();
  }
  async get(url) {
    if (this.cache.has(url))
      return this.cache.get(url);
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        this.cache.set(url, image);
        resolve(image);
      };
      image.src = url;
    });
  }
};

// src/core/PdfGutsPreviewWorker.ts
var PdfGutsPreviewWorker = class extends PdfPreviewWorker {
  constructor(canvas, data) {
    super(canvas);
    this.canvas = canvas;
    this.data = data;
    this.imageCache = new ImageCache();
  }
  static async init(canvas, data) {
    return new PdfGutsPreviewWorker(canvas, data);
  }
  async render(index) {
    const pages = this.data.pages[index];
    if (!pages) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return console.warn("Page index out of bounds.");
    }
    this.canvas.width = this.data.dimensions.odd.width + this.data.dimensions.even.width;
    this.canvas.height = Math.max(this.data.dimensions.odd.height, this.data.dimensions.even.height);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    (await Promise.all([this.loadPageImage(pages[0]), this.loadPageImage(pages[1])])).map(
      ({ page, image }) => this.renderPage(page, image)
    );
    if (this.settings.showSafeZone) {
      this.renderSafeZone(pages);
    }
    if (this.settings.showBleed) {
      this.renderBleed(pages);
    }
  }
  async loadPageImage(page) {
    if (!page)
      return {};
    const image = await this.imageCache.get(page.imageUrl);
    return { page, image };
  }
  renderPage(page, image) {
    if (!page || !image)
      return;
    this.ctx.save();
    this.ctx.shadowColor = PdfPreviewWorker.CONFIGURATION.SHADOW.COLOR;
    this.ctx.shadowBlur = PdfPreviewWorker.CONFIGURATION.SHADOW.BLUR;
    this.ctx.shadowOffsetX = 0;
    this.ctx.shadowOffsetY = 0;
    const dimensions = this.data.dimensions[page.type];
    this.ctx.drawImage(image, dimensions.left, dimensions.top, dimensions.width, dimensions.height);
    this.ctx.restore();
  }
  renderSafeZone(pages) {
    const drawSingle = (page, side) => {
      this.drawSafeZone(
        {
          left: side === "left" ? this.data.safeZone.outside : this.data.safeZone.inside,
          top: this.data.safeZone.top,
          right: side === "left" ? this.data.safeZone.inside : this.data.safeZone.outside,
          bottom: this.data.safeZone.bottom
        },
        this.data.dimensions[page.type]
      );
    };
    if (pages[0])
      drawSingle(pages[0], "left");
    if (pages[1])
      drawSingle(pages[1], "right");
  }
  renderBleed(pages) {
    const drawSingle = (page, side) => {
      this.drawBleed(
        {
          left: side === "left" ? this.data.bleed.outside : this.data.bleed.inside,
          top: this.data.bleed.top,
          right: side === "left" ? this.data.bleed.inside : this.data.bleed.outside,
          bottom: this.data.bleed.bottom
        },
        this.data.dimensions[page.type]
      );
    };
    if (pages[0])
      drawSingle(pages[0], "left");
    if (pages[1])
      drawSingle(pages[1], "right");
  }
};

// src/helpers/cover.helper.ts
function getCoverDataFromPdfProcessingCollection(collection) {
  const imageUrl = collection.previews.cover.cover_preview.outside_full_cover.src;
  const dimensions = {
    width: collection.previews.definitions.cover.main_content_pre_trim.width,
    height: collection.previews.definitions.cover.main_content_pre_trim.height
  };
  const bleed = collection.inline_product.specification.cover_spec.bleeds;
  const safeZone = objectsSum(bleed, collection.inline_product.specification.cover_spec.safe_zone);
  const definitions = collection.previews.definitions.cover;
  const sections = Object.keys(definitions).filter((key) => !definitions[key].empty).reduce((previous, key) => {
    const section = definitions[key];
    previous[key] = {
      top: section.top,
      bottom: section.bottom,
      left: section.left,
      right: section.right
    };
    return previous;
  }, {});
  return {
    imageUrl,
    dimensions,
    bleed,
    safeZone,
    sections
  };
}

// src/helpers/guts.helper.ts
function getGutsDataFromPdfProcessingCollection(collection) {
  const definitions = collection.previews.definitions.pages;
  const pagesSpec = collection.inline_product.specification.pages_spec;
  const previewsPages = collection.previews.pages;
  const dimensions = {
    even: {
      top: 0,
      left: 0,
      width: definitions.even_page_content.width,
      height: definitions.even_page_content.height
    },
    odd: {
      top: 0,
      left: definitions.even_page_content.width,
      width: definitions.odd_page_content.width,
      height: definitions.odd_page_content.height
    }
  };
  const bleed = pagesSpec.bleeds;
  const safeZone = objectsSum(pagesSpec.bleeds, pagesSpec.safe_zone);
  const pages = [];
  let group = Array(2).fill(void 0);
  for (let i = 0; i < previewsPages.length; i++) {
    const page = previewsPages[i];
    group[page.type === "even" ? 0 : 1] = {
      type: page.type,
      imageUrl: page.previews.guts_preview,
      thumbnailUrl: page.previews.guts_thumbnails
    };
    if (page.type === "odd" || i >= previewsPages.length - 1) {
      pages.push(group);
      group = Array(2).fill(void 0);
    }
  }
  return {
    dimensions,
    bleed,
    safeZone,
    pages
  };
}
export {
  PdfCoverPreviewWorker,
  PdfGutsPreviewWorker,
  PdfPreviewWorker,
  getCoverDataFromPdfProcessingCollection,
  getGutsDataFromPdfProcessingCollection
};
